/*
  Dashboard, main page of the website.
  It consists of 4 components : device tab, navbar, images and video
*/

import { useState, useEffect } from "react";
import Navbar from '../components/Navbar';
import FileExplorer from '../components/FileExplorer';
import { getDynamoDBClient } from '../utils/awsClients';
import { fetchAuthSession } from 'aws-amplify/auth';
import { deviceScanParams, groupsFromSession, dedupeDevices } from '../utils/access';
import DeviceTabs from "../components/DeviceTab";
import VideoDisplay from "../components/VideoDisplay";
import ImageDisplay from "../components/ImageDisplay";
import DeviceStatus from "../components/DeviceStatus";

function Dashboard() {
  const savedDevice = JSON.parse(localStorage.getItem('selectedDevice'));
  const [userDevices, setUserDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(savedDevice || null);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [selectedSample, setSelectedSample] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  const handleSnapshotOpen = (snapshot) => {
    setSelectedSnapshot(snapshot);
    setSelectedExperiment(null); // reset experiment flow
    setSelectedSample(null);
  };


  // Fetch DynamoDB data and set selectedDevice from localStorage if available
  const fetchDynamoDBData = async () => {
    const userData = JSON.parse(localStorage.getItem('userData'));
    try {
      const dynamoDB = await getDynamoDBClient();

      // Fleet-wide access is granted by membership of the `super-admins` Cognito
      // group, NOT by the user's bucket name starting with "Triton" (that old
      // check handed every user of the shared TritonBio-* bucket access to all
      // devices). See utils/access.js.
      const session = await fetchAuthSession();
      const params = deviceScanParams({
        groups: groupsFromSession(session),
        userBucket: userData?.['custom:bucket'],
      });

      const result = await dynamoDB.scan(params).promise();
      // One physical device can hold a registration per access group, so a
      // super-admin's unfiltered scan returns it once per group and the list
      // shows the same unit twice. Collapse on device_bucket before sorting.
      const sortedDevices = dedupeDevices(result.Items).sort((a, b) => {
        if (a.device_name < b.device_name) return -1;
        if (a.device_name > b.device_name) return 1;
        return 0;
      });
      setUserDevices(sortedDevices);

      // Pick the default from the SAME list the tabs render. DeviceTabs matches
      // the selection by MIRA_device_id, so choosing out of the raw scan can
      // select a row that de-duplication dropped -- the tab value then matches
      // no tab and nothing appears selected. (Ponyta's two rows differ only by
      // casing, `ponyta` vs `Ponyta`, and scan order is not guaranteed.)
      let defaultDevice = null;
      if (savedDevice) {
        const parsedDevice = savedDevice;
        defaultDevice = sortedDevices.find(device => device.device_id === parsedDevice.device_id);
      }
      if (!defaultDevice && sortedDevices.length > 0) {
        defaultDevice = sortedDevices[0];
      }
      if (defaultDevice) {
        setSelectedDevice(defaultDevice);
        localStorage.setItem('selectedDevice', JSON.stringify(defaultDevice));
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch device data:", error);
      setIsLoading(false);
    }
  };

  const handleDeviceSelection = (device) => {
    setSelectedDevice(device);
    localStorage.setItem('selectedDevice', JSON.stringify(device));
  };

  const handleSampleOpen = (sample_name) => {
    setSelectedSample(sample_name);
    setSelectedSnapshot(null); // reset snapshot mode
  };


  const handleExperimentOpen = (experiment) => {
    setSelectedExperiment(experiment);
    setSelectedSnapshot(null); // reset snapshot mode
  };

  const handleImageSelection = (item) => {
    setSelectedImage(item);
  };

  useEffect(() => {
    fetchDynamoDBData();
    setSelectedSnapshot(null);
  }, []);

  useEffect(() => {
    setSelectedExperiment(null);
    setSelectedSample(null);
    setSelectedSnapshot(null);
    setSelectedImage(null);
  }, [selectedDevice]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className='dashboard-container'>
      <Navbar />
      <DeviceTabs
        devices={userDevices}
        selectedDevice={selectedDevice}
        onDeviceSelect={handleDeviceSelection}
      />
      <div className='main-container'>
        <div className='file-display'>
          {selectedDevice && (
            <FileExplorer
              key={selectedDevice.device_id}
              device={selectedDevice}
              onClickSample={handleSampleOpen}
              onClickOpenExperiment={handleExperimentOpen}
              onClickSnapshot={handleSnapshotOpen}
              handleImageSelection={handleImageSelection}
            />

          )}
        </div>
        <div className='image-container'>
          {selectedDevice && <DeviceStatus selectedDevice={selectedDevice} />}

          {/* Show video only if no image view is active */}
          {!selectedSample && !selectedSnapshot && (
            <VideoDisplay
              selectedDevice={selectedDevice}
              selectedExperiment={selectedExperiment}
              selectedImage={selectedImage}
            />
          )}

          {/* Show image if sample or snapshot is selected */}
          {(selectedSample || selectedSnapshot) && (
            <ImageDisplay
              selectedDevice={selectedDevice}
              selectedExperiment={selectedExperiment}
              selectedSample={selectedSample}
              selectedSnapshot={selectedSnapshot}
              selectedImage={selectedImage}
            />
          )}
        </div>

      </div>
    </div>
  );
}

export default Dashboard;
